import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { css } from 'aphrodite';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ProductImage } from 'components/brand_sku_mapper_shared/ProductImage';
import { galleryStyles as styles } from 'components/brand_sku_mapper_shared/galleryStyles';
import { uniqueImageUrls } from 'components/brand_sku_mapper_shared/imageHelpers';

// Navigable gallery of a product's own images (thumbnail first) for the zoom modal. Shared by the
// MFR->DP and v2 review views -- pass the product's thumbnail_url + image_urls.
export function ZoomImageGallery({ thumbnailUrl, imageUrls }) {
  const images = useMemo(
    () => uniqueImageUrls([thumbnailUrl, ...(imageUrls || [])]),
    [thumbnailUrl, imageUrls],
  );
  const count = images.length;

  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [thumbnailUrl]);

  const go = useCallback(delta => setIdx(i => (count ? (i + delta + count) % count : 0)), [count]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  const safeIdx = count ? Math.min(idx, count - 1) : 0;

  // Preload neighbours so prev/next swaps are instant.
  useEffect(() => {
    if (count <= 1) {
      return;
    }
    [safeIdx - 1, safeIdx + 1, safeIdx + 2].forEach(n => {
      const url = images[((n % count) + count) % count];
      if (url) {
        new window.Image().src = url;
      }
    });
  }, [images, safeIdx, count]);

  return (
    <div className={css(styles.galleryWrap)}>
      <div className={css(styles.galleryImageRow)}>
        {count > 1 && (
          <button
            type="button"
            className={css(styles.galleryNav, styles.galleryNavLeft)}
            onClick={() => go(-1)}
            aria-label="Previous image"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
        )}
        <ProductImage
          src={images[safeIdx]}
          imgClassName={css(styles.zoomImage)}
          placeholderClassName={css(styles.zoomImagePlaceholder)}
          placeholderText={
            <span className={css(styles.galleryPlaceholderText)}>No image available</span>
          }
        />
        {count > 1 && (
          <button
            type="button"
            className={css(styles.galleryNav, styles.galleryNavRight)}
            onClick={() => go(1)}
            aria-label="Next image"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}
      </div>
      {count > 0 && <div className={css(styles.galleryCounter)}>{`${safeIdx + 1} / ${count}`}</div>}
    </div>
  );
}

ZoomImageGallery.propTypes = {
  thumbnailUrl: PropTypes.string,
  imageUrls: PropTypes.arrayOf(PropTypes.string),
};
